import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { toast } from "sonner";
import { Save, Truck, Box, Bell as BellIcon, MessageSquare, FileText, Plus, PhoneIncoming, ClipboardList, Loader2 } from "lucide-react";
import { PICKUP_STATUSES, PICKUP_STATUS_LABELS, PickupStatusBadge } from "./PickupSection";
import WhatsAppTemplateButton from "../WhatsAppTemplateButton";
import SendPickupConfirmationButton from "../SendPickupConfirmationButton";
import DatePickerField from "../DatePickerField";
import DispatchPanel from "../DispatchPanel";
import NAWAddressBlock from "./NAWAddressBlock";
import { PickupFreightEditor } from "./PickupFreightEditor";
import { calculationFrom, pickupCalculationPayload, pickupDraftError } from "./pickupCalculationHelpers";

function _composeAddress(street, house_number, addition) {
  const parts = [];
  if (street) parts.push(street);
  const hn = [house_number, addition].filter(Boolean).join("");
  if (hn) parts.push(hn);
  return parts.join(" ").trim();
}

function fmtTs(ts) { return ts?.slice(0, 16).replace("T", " "); }

export default function PickupDetail({ pickupId, onChanged }) {
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [shipment, setShipment] = useState({});
  const [pricing, setPricing] = useState({});
  const [calculation, setCalculation] = useState(calculationFrom({}));
  const [calculated, setCalculated] = useState(null);
  const [calculationError, setCalculationError] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("klantgegevens");
  const [newNote, setNewNote] = useState("");
  // Operationele P0 — reminder-add dialog + call-logs lijst op pickup
  const [reminderDlg, setReminderDlg] = useState(null);  // null of {date, time, ...}
  const [callLogs, setCallLogs] = useState([]);
  // Pickup & Delivery Fase 3 — PDF download busy states
  const [pdfBusy, setPdfBusy] = useState(null);  // 'klant' | 'werkbon' | null

  const canEdit = hasPermission("pickup:edit");
  // Pickup & Delivery Fase 3 — RBAC voor e-mail naar klant.
  // Medewerker mag PDF/werkbon downloaden + WhatsApp tekst genereren,
  // maar GEEN e-mail naar klant versturen (volgens sprint-scope).
  const userRole = (user?.role || "").toLowerCase();
  const canEmailCustomer = userRole !== "medewerker";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/pickup/${pickupId}`);
      setData(r.data);
      const p = r.data.pickup;
      setForm({
        customer_name: p.customer_name || "", company_name: p.company_name || "",
        email: p.email || "", phone: p.phone || "", whatsapp: p.whatsapp || "",
        pickup_address: p.pickup_address || "", pickup_postal_code: p.pickup_postal_code || "",
        pickup_city: p.pickup_city || "", pickup_country: p.pickup_country || "",
        pickup_street: p.pickup_street || p.pickup_address || "",
        pickup_house_number: p.pickup_house_number || "",
        pickup_house_number_addition: p.pickup_house_number_addition || "",
        pickup_date: p.pickup_date || "", pickup_time: p.pickup_time || "",
        pickup_instructions: p.pickup_instructions || "",
        delivery_address: p.delivery_address || "", delivery_postal_code: p.delivery_postal_code || "",
        delivery_city: p.delivery_city || "", delivery_country: p.delivery_country || "",
        delivery_street: p.delivery_street || p.delivery_address || "",
        delivery_house_number: p.delivery_house_number || "",
        delivery_house_number_addition: p.delivery_house_number_addition || "",
        delivery_date: p.delivery_date || "", delivery_time: p.delivery_time || "",
        delivery_instructions: p.delivery_instructions || "",
      });
      setShipment(p.shipment || {});
      setPricing(p.pricing || {});
      setCalculation(calculationFrom(p));
      setCalculated(p);
      setCalculationError("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [pickupId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return undefined;
    const incomplete = pickupDraftError(calculation) || (calculation.quote_basis === "total_volume" && !(Number(pricing.price_per_cbm) > 0) ? "Vul een positief tarief per m³ in." : "");
    if (incomplete) {
      setCalculationError(incomplete); setCalculating(false);
      return undefined;
    }
    const abort = new AbortController();
    setCalculating(true);
    const timer = setTimeout(() => {
      adminApi.post("/admin/pickup-calculation", pickupCalculationPayload(calculation, shipment, pricing),
        { params: { pickup_id: pickupId }, signal: abort.signal })
        .then(r => { setCalculated(r.data); setCalculationError(""); })
        .catch(e => { if (!abort.signal.aborted) setCalculationError(formatApiError(e)); })
        .finally(() => { if (!abort.signal.aborted) setCalculating(false); });
    }, 250);
    return () => { clearTimeout(timer); abort.abort(); };
  }, [calculation, shipment, pricing, pickupId, data]);

  // Operationele P0 — laad gekoppelde call-logs voor dit pickup
  useEffect(() => {
    if (!pickupId) return;
    adminApi.get(`/admin/pickup/${pickupId}/call-logs`)
      .then((r) => setCallLogs(r.data || []))
      .catch(() => setCallLogs([]));
  }, [pickupId]);

  const onSave = async () => {
    const incomplete = pickupDraftError(calculation);
    if (incomplete) { toast.error(incomplete); return; }
    setSaving(true);
    try {
      // P1 — Houd pickup_address & delivery_address gesynchroniseerd met de
      // gestructureerde NAW-velden (achterwaarts compat met oude weergaven/PDF).
      const composedPickup = _composeAddress(form.pickup_street, form.pickup_house_number, form.pickup_house_number_addition);
      const composedDelivery = _composeAddress(form.delivery_street, form.delivery_house_number, form.delivery_house_number_addition);
      const merged = {
        ...form,
        pickup_address: composedPickup || form.pickup_address || "",
        delivery_address: composedDelivery || form.delivery_address || "",
      };
      // P0 Bugfix 3 Mrt 2026 — Datum- en tijd-velden mogen NIET worden gefilterd
      // uit de payload wanneer ze leeg zijn. Als een gebruiker een tijd wist,
      // moet die wijziging worden doorgevoerd (backend interpreteert "" als
      // wissen). Anders bleef de oude tijd (bijv. "12:30") staan omdat het
      // veld niet werd meegestuurd.
      const ALWAYS_SEND = new Set([
        "pickup_date", "pickup_time",
        "delivery_date", "delivery_time",
      ]);
      const payload = {
        ...Object.fromEntries(
          Object.entries(merged).filter(([k, v]) => v !== "" || ALWAYS_SEND.has(k)),
        ),
        ...pickupCalculationPayload(calculation, shipment, pricing),
      };
      await adminApi.put(`/admin/pickup/${pickupId}`, payload);
      toast.success("Afspraak bijgewerkt");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onChangeStatus = async (status) => {
    try {
      await adminApi.put(`/admin/pickup/${pickupId}/status`, { status });
      load(); onChanged?.();
      toast.success("Status bijgewerkt");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await adminApi.post(`/admin/pickup/${pickupId}/notes`, { text: newNote });
      setNewNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Pickup & Delivery Fase 3 — Download de klant-PDF of werkbon-PDF.
  // Backend logt zelf de audit-events pickup_pdf_exported /
  // pickup_workorder_generated. Frontend laat alleen de toast zien.
  const downloadPdf = async (variant) => {
    setPdfBusy(variant);
    const isWorkorder = variant === "werkbon";
    const path = isWorkorder
      ? `/admin/pickup/${pickupId}/workorder.pdf`
      : `/admin/pickup/${pickupId}/pdf`;
    const fname = isWorkorder
      ? `werkbon-${pickupId.slice(0, 8)}.pdf`
      : `pickup-${pickupId.slice(0, 8)}.pdf`;
    try {
      const r = await adminApi.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(isWorkorder ? "Werkbon gedownload" : "Klant-PDF gedownload");
    } catch (e) {
      toast.error(formatApiError(e) || "PDF kon niet worden gegenereerd");
    } finally {
      setPdfBusy(null);
    }
  };

  if (loading || !data) return <div className="text-gray-500">Afspraak laden...</div>;
  const p = data.pickup;
  const reminders = data.reminders || [];
  const notes = data.notes || [];
  const customer = data.customer;
  const usesLineVolume = !!calculation.quote_basis && calculation.line_items.length > 0;

  const num = (label, key, target, setter, suffix) => (
    <div>
      <Label className="text-xs uppercase tracking-wide font-bold">{label}{suffix && <span className="text-gray-400 normal-case font-normal"> ({suffix})</span>}</Label>
      <Input
        type="number" step="0.01" value={target[key] ?? ""}
        onChange={(e) => setter({ ...target, [key]: e.target.value === "" ? null : parseFloat(e.target.value),
          ...(key === "cbm" ? { cbm_mode: e.target.value === "" ? "auto" : "manual" } : {}) })}
        disabled={!canEdit || (key === "cbm" && usesLineVolume)} className="rounded-none mt-1"
        data-testid={`pickup-${key}`}
      />
    </div>
  );

  return (
    <div data-testid={`pickup-detail-${pickupId}`} className="pickup-detail space-y-4">
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-bold">Pickup & Delivery</div>
            <h1 className="font-chivo font-black text-2xl text-[#0A192F] mt-1 flex items-center gap-2">
              <Truck className="w-6 h-6 text-[#0055FF]" />{p.customer_name || "Onbekend"}
            </h1>
            <div className="text-xs text-gray-500 mt-2">
              ID: <span className="font-mono">{p.id}</span> · Aangemaakt {fmtTs(p.created_at)}
              {p.quote_id && <> · Uit offerte {p.quote_id.slice(0,8)}…</>}
              {p.source && <> · Bron: {p.source}</>}
            </div>
            {customer && <div className="text-xs text-[#0055FF] mt-1">Klant: {customer.email || customer.id}</div>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PickupStatusBadge status={p.status} />
            {canEdit && (
              <Select value={p.status} onValueChange={onChangeStatus}>
                <SelectTrigger className="rounded-none w-56" data-testid="pickup-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PICKUP_STATUSES.map((s) => <SelectItem key={s} value={s}>{PICKUP_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Pickup & Delivery Fase 3 — Acties (PDF / Werkbon / E-mail / WhatsApp). */}
      <div
        className="bg-white border border-gray-200 p-4 sm:p-5"
        data-testid="pickup-actions-panel"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#0055FF]" />
            <h3 className="font-chivo font-black text-sm uppercase tracking-wide text-[#0A192F]">
              Acties
            </h3>
          </div>
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-bold">
            Afspraakdossier delen, mailen en bewerken
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button
            onClick={() => downloadPdf("klant")}
            disabled={pdfBusy === "klant"}
            className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-none font-chivo font-bold uppercase text-xs h-12"
            data-testid="pickup-action-download-pdf"
          >
            {pdfBusy === "klant" ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-1.5" />
            )}
            PDF (klant)
          </Button>
          <Button
            onClick={() => downloadPdf("werkbon")}
            disabled={pdfBusy === "werkbon"}
            variant="outline"
            className="rounded-none font-chivo font-bold uppercase text-xs h-12 border-[#082c50] text-[#082c50] hover:bg-blue-50"
            data-testid="pickup-action-download-workorder"
          >
            {pdfBusy === "werkbon" ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <ClipboardList className="w-4 h-4 mr-1.5" />
            )}
            Werkbon
          </Button>
          {canEmailCustomer ? (
            <div data-testid="pickup-action-email-wrapper" className="contents">
              <SendPickupConfirmationButton pickupId={p.id} defaultEmail={p.email || customer?.email || ""} appointment={p} />
            </div>
          ) : (
            <div
              className="flex items-center justify-center text-[10px] text-gray-400 italic border border-dashed border-gray-200 rounded-none h-12 px-2 text-center"
              data-testid="pickup-action-email-readonly"
            >
              E-mail naar klant: Manager+
            </div>
          )}
          <WhatsAppTemplateButton
            pickupId={p.id}
            customerId={p.customer_id}
            testIdPrefix="pickup-wa"
          />
        </div>
      </div>

      {/* Sprint jun 2026 — bidirectionele koppeling: gekoppelde
          telefoonregistraties zijn direct zichtbaar op de afspraak. */}
      {callLogs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-3" data-testid="pickup-call-logs-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <PhoneIncoming className="w-4 h-4 text-[#0055FF]" />
            <span className="text-[11px] uppercase font-bold tracking-wide text-[#0A192F]">
              {callLogs.length} gekoppelde telefoonregistratie{callLogs.length === 1 ? "" : "s"}
            </span>
            <button
              type="button" onClick={() => setTab("reminders")}
              className="ml-auto text-[10px] uppercase font-bold text-[#0055FF] hover:underline"
              data-testid="pickup-call-logs-banner-open"
            >
              Alles bekijken
            </button>
          </div>
          <ul className="mt-2 space-y-1">
            {callLogs.slice(0, 3).map((c) => (
              <li key={c.id} className="text-xs text-gray-700 break-words"
                  data-testid={`pickup-call-log-banner-${c.id}`}>
                <span className="font-bold text-[#082c50]">{c.subject}</span>
                {" · "}{c.customer_name || "—"}
                {c.phone ? ` · ${c.phone}` : ""}
                {" · "}{new Date(c.created_at).toLocaleDateString("nl-NL")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-white border border-gray-200 p-0 h-auto flex flex-wrap" data-testid="pickup-tabs">
          <TabsTrigger value="klantgegevens" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-klant">Klantgegevens</TabsTrigger>
          <TabsTrigger value="pickup" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-pickup">Pickup</TabsTrigger>
          <TabsTrigger value="delivery" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-delivery">Delivery</TabsTrigger>
          <TabsTrigger value="zending" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-zending">Zending</TabsTrigger>
          <TabsTrigger value="prijs" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-prijs">Vrachtcalculator</TabsTrigger>
          <TabsTrigger value="dispatch" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-dispatch">Dispatch</TabsTrigger>
          <TabsTrigger value="reminders" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-reminders">Reminders ({reminders.length})</TabsTrigger>
          <TabsTrigger value="notities" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="pickup-tab-notities">Notities ({notes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="klantgegevens" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4">Klantgegevens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[["customer_name","Naam klant"],["company_name","Bedrijfsnaam"],["email","E-mail"],["phone","Telefoon"],["whatsapp","WhatsApp"]].map(([k,l]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide font-bold">{l}</Label>
                <Input value={form[k] || ""} onChange={(e)=>setForm({...form,[k]:e.target.value})} disabled={!canEdit} className="rounded-none mt-1" data-testid={`pickup-${k}`} />
              </div>
            ))}
          </div>
          {canEdit && <Button onClick={onSave} disabled={saving} className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />Opslaan</Button>}
        </TabsContent>

        <TabsContent value="pickup" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4">Pickup details</h2>
          <NAWAddressBlock
            value={{
              street: form.pickup_street,
              house_number: form.pickup_house_number,
              house_number_addition: form.pickup_house_number_addition,
              postal_code: form.pickup_postal_code,
              city: form.pickup_city,
              country: form.pickup_country,
            }}
            onChange={(patch) => setForm((prev) => {
              const next = { ...prev };
              if ("street" in patch) next.pickup_street = patch.street;
              if ("house_number" in patch) next.pickup_house_number = patch.house_number;
              if ("house_number_addition" in patch) next.pickup_house_number_addition = patch.house_number_addition;
              if ("postal_code" in patch) next.pickup_postal_code = patch.postal_code;
              if ("city" in patch) next.pickup_city = patch.city;
              if ("country" in patch) next.pickup_country = patch.country;
              return next;
            })}
            disabled={!canEdit}
            testidPrefix="pickup-naw"
            title="Ophaaladres (postcode + huisnummer voor PDOK-lookup)"
            hint="Vul NL postcode + huisnummer in voor automatische straat/plaats."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div><Label className="text-xs uppercase tracking-wide font-bold">Datum</Label><Input type="date" value={form.pickup_date||""} onChange={(e)=>setForm({...form,pickup_date:e.target.value})} disabled={!canEdit} className="rounded-none mt-1" data-testid="pickup-pickup_date"/></div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Tijd</Label>
              <div className="flex gap-2 items-stretch mt-1">
                <Input type="time" value={form.pickup_time||""} onChange={(e)=>setForm({...form,pickup_time:e.target.value})} disabled={!canEdit} className="rounded-none flex-1" data-testid="pickup-pickup_time"/>
                {canEdit && form.pickup_time ? (
                  <button
                    type="button"
                    onClick={()=>setForm({...form,pickup_time:""})}
                    className="px-3 border border-gray-300 bg-white hover:bg-gray-50 text-xs font-chivo font-bold uppercase text-gray-700"
                    data-testid="pickup-pickup_time-clear"
                    title="Tijd wissen"
                  >Wissen</button>
                ) : null}
              </div>
            </div>
          </div>
          <div className="mt-3"><Label className="text-xs uppercase tracking-wide font-bold">Extra instructies pickup</Label><Textarea rows={3} value={form.pickup_instructions||""} onChange={(e)=>setForm({...form,pickup_instructions:e.target.value})} disabled={!canEdit} className="rounded-none mt-1"/></div>
          {canEdit && <Button onClick={onSave} disabled={saving} className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />Opslaan</Button>}
        </TabsContent>

        <TabsContent value="delivery" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4">Delivery details</h2>
          <NAWAddressBlock
            value={{
              street: form.delivery_street,
              house_number: form.delivery_house_number,
              house_number_addition: form.delivery_house_number_addition,
              postal_code: form.delivery_postal_code,
              city: form.delivery_city,
              country: form.delivery_country,
            }}
            onChange={(patch) => setForm((prev) => {
              const next = { ...prev };
              if ("street" in patch) next.delivery_street = patch.street;
              if ("house_number" in patch) next.delivery_house_number = patch.house_number;
              if ("house_number_addition" in patch) next.delivery_house_number_addition = patch.house_number_addition;
              if ("postal_code" in patch) next.delivery_postal_code = patch.postal_code;
              if ("city" in patch) next.delivery_city = patch.city;
              if ("country" in patch) next.delivery_country = patch.country;
              return next;
            })}
            disabled={!canEdit}
            testidPrefix="delivery-naw"
            title="Afleveradres (postcode + huisnummer voor PDOK-lookup, NL)"
            hint="Voor NL adressen worden straat en plaats automatisch aangevuld."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div><Label className="text-xs uppercase tracking-wide font-bold">Datum</Label><Input type="date" value={form.delivery_date||""} onChange={(e)=>setForm({...form,delivery_date:e.target.value})} disabled={!canEdit} className="rounded-none mt-1" data-testid="pickup-delivery_date"/></div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Tijd</Label>
              <div className="flex gap-2 items-stretch mt-1">
                <Input type="time" value={form.delivery_time||""} onChange={(e)=>setForm({...form,delivery_time:e.target.value})} disabled={!canEdit} className="rounded-none flex-1" data-testid="pickup-delivery_time"/>
                {canEdit && form.delivery_time ? (
                  <button
                    type="button"
                    onClick={()=>setForm({...form,delivery_time:""})}
                    className="px-3 border border-gray-300 bg-white hover:bg-gray-50 text-xs font-chivo font-bold uppercase text-gray-700"
                    data-testid="pickup-delivery_time-clear"
                    title="Tijd wissen"
                  >Wissen</button>
                ) : null}
              </div>
            </div>
          </div>
          <div className="mt-3"><Label className="text-xs uppercase tracking-wide font-bold">Extra instructies delivery</Label><Textarea rows={3} value={form.delivery_instructions||""} onChange={(e)=>setForm({...form,delivery_instructions:e.target.value})} disabled={!canEdit} className="rounded-none mt-1"/></div>
          {canEdit && <Button onClick={onSave} disabled={saving} className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />Opslaan</Button>}
        </TabsContent>

        <TabsContent value="zending" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-4 flex items-center gap-2"><Box className="w-5 h-5 text-[#0055FF]" />Zending</h2>
          <div className="mb-4 flex flex-wrap items-center gap-3"><label className="text-sm">CBM-berekening<select className="max-w-full border p-2 bg-white" disabled={!canEdit || usesLineVolume} value={usesLineVolume ? "lines" : shipment.cbm_mode === "lines" ? "manual" : shipment.cbm_mode || (shipment.cbm == null ? "auto" : "manual")} onChange={e => setShipment({ ...shipment, cbm_mode: e.target.value })} data-testid="pickup-volume-mode"><option value="auto">Automatisch uit afmetingen</option><option value="manual">Handmatig totaal</option><option value="lines" disabled>Uit calculatieregels</option></select></label><strong className="text-sm" data-testid="pickup-shipment-volume">{Number(calculated?.shipment?.cbm || 0).toFixed(4)} m³</strong></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {num("Aantal dozen", "boxes", shipment, setShipment)}
            {num("Aantal pakketten", "packages", shipment, setShipment)}
            {num("Aantal pallets", "pallets", shipment, setShipment)}
            {num("Totaal kg", "total_kg", shipment, setShipment, "kg")}
            {num("CBM", "cbm", shipment, setShipment, "m³")}
            {num("Lengte", "length_cm", shipment, setShipment, "cm")}
            {num("Breedte", "width_cm", shipment, setShipment, "cm")}
            {num("Hoogte", "height_cm", shipment, setShipment, "cm")}
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Breekbaar</Label>
              <Select value={shipment.fragile==null?"":String(shipment.fragile)} onValueChange={(v)=>setShipment({...shipment,fragile:v==="true"})} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent><SelectItem value="true">Ja</SelectItem><SelectItem value="false">Nee</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Hulp bij tillen</Label>
              <Select value={shipment.needs_lifting_help==null?"":String(shipment.needs_lifting_help)} onValueChange={(v)=>setShipment({...shipment,needs_lifting_help:v==="true"})} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent><SelectItem value="true">Ja</SelectItem><SelectItem value="false">Nee</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Extra medewerker nodig</Label>
              <Select value={shipment.needs_extra_helper==null?"":String(shipment.needs_extra_helper)} onValueChange={(v)=>setShipment({...shipment,needs_extra_helper:v==="true"})} disabled={!canEdit}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent><SelectItem value="true">Ja</SelectItem><SelectItem value="false">Nee</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3"><Label className="text-xs uppercase tracking-wide font-bold">Omschrijving goederen</Label><Textarea rows={3} value={shipment.description||""} onChange={(e)=>setShipment({...shipment,description:e.target.value})} disabled={!canEdit} className="rounded-none mt-1"/></div>
          {canEdit && <Button onClick={onSave} disabled={saving} className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />Opslaan</Button>}
        </TabsContent>

        <TabsContent value="prijs" className="bg-white border border-gray-200 p-6 mt-2">
          <PickupFreightEditor value={calculation} onChange={setCalculation} pricing={pricing} setPricing={setPricing} canEdit={canEdit && !saving} />
          {!calculation.quote_basis && <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {num("Aantal km", "distance_km", pricing, setPricing, "km")}
            {num("KM-vergoeding", "km_rate", pricing, setPricing, "EUR/km")}
            {num("Voorrijkosten", "callout_fee", pricing, setPricing, "EUR")}
            {num("Korting", "base_pickup_fee", pricing, setPricing, "EUR")}
            {num("Extra handlingkosten", "extra_handling", pricing, setPricing, "EUR")}
            {num("Wachtkosten", "waiting_fee", pricing, setPricing, "EUR")}
            {num("Parkeerkosten", "parking_fee", pricing, setPricing, "EUR")}
            {num("Btw %", "vat_percent", pricing, setPricing, "%")}
          </div>}
          {calculationError && <p role="alert" className="text-red-700 text-sm mt-4" data-testid="pickup-calculation-error">{calculationError}</p>}
          {calculating && <p role="status" className="text-gray-500 text-sm mt-4" data-testid="pickup-calculating">Berekenen…</p>}
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs uppercase tracking-wide text-gray-500">Totaalvolume</div><div className="font-chivo font-bold mt-1" data-testid="pickup-calculated-volume">{Number(calculated?.shipment?.cbm || 0).toFixed(4)} m³</div></div>
            <div><div className="text-xs uppercase tracking-wide text-gray-500">Subtotaal excl. btw</div><div className="font-chivo font-bold mt-1" data-testid="pickup-subtotal">{calculationError || calculating ? "—" : `€ ${Number(calculated?.pricing?.subtotal_excl_vat || 0).toFixed(2)}`}</div></div>
            <div><div className="text-xs uppercase tracking-wide text-gray-500">Btw</div><div className="font-chivo font-bold mt-1" data-testid="pickup-vat">{calculationError || calculating ? "—" : `€ ${Number(calculated?.pricing?.vat_amount || 0).toFixed(2)}`}</div></div>
            <div><div className="text-xs uppercase tracking-wide text-gray-500">Totaal incl. btw</div><div className="font-chivo font-black text-xl text-[#0A192F] mt-1" data-testid="pickup-total">{calculationError || calculating ? "—" : `€ ${Number(calculated?.pricing?.total_incl_vat || 0).toFixed(2)}`}</div></div>
          </div>
          {canEdit && <Button onClick={onSave} disabled={saving || calculating || !!calculationError} data-testid="pickup-calculation-save" className="mt-4 bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"><Save className="w-4 h-4 mr-1" />Bereken & opslaan</Button>}
        </TabsContent>

        <TabsContent value="dispatch" className="mt-2 space-y-4">
          {/* Toon pickup-leg en delivery-leg los van elkaar zodat de medewerker
              duidelijk ziet voor welke afspraak hij de chauffeur dispatched.
              Beide gebruiken hetzelfde appointment-id maar verschillende kind. */}
          {(p.pickup_address || p.pickup_date) && (
            <DispatchPanel appointment={p} kind="pickup" />
          )}
          {(p.delivery_address || p.delivery_date) && (
            <DispatchPanel appointment={p} kind="delivery" />
          )}
          {!(p.pickup_address || p.pickup_date || p.delivery_address || p.delivery_date) && (
            <div className="bg-white border border-gray-200 p-6 text-sm text-gray-500">
              Voeg eerst een pickup- of delivery-adres toe om een chauffeur te kunnen dispatchen.
            </div>
          )}
        </TabsContent>

        <TabsContent value="reminders" className="bg-white border border-gray-200 p-6 mt-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-chivo font-black text-lg text-[#0A192F] flex items-center gap-2"><BellIcon className="w-5 h-5 text-[#0055FF]" />Reminders ({reminders.length})</h2>
            {canEdit && (
              <Button size="sm" onClick={() => {
                // Prefill: 1 dag vóór pickup_date, anders morgen
                const baseDate = form.pickup_date;
                let prefillDate = "";
                if (baseDate) {
                  try {
                    const d = new Date(baseDate);
                    d.setDate(d.getDate() - 1);
                    prefillDate = d.toISOString().slice(0, 10);
                    // Niet in het verleden: val terug op pickup-dag
                    const today = new Date().toISOString().slice(0, 10);
                    if (prefillDate < today) prefillDate = baseDate;
                  } catch (_) { /* keep empty */ }
                } else {
                  const t = new Date(); t.setDate(t.getDate() + 1);
                  prefillDate = t.toISOString().slice(0, 10);
                }
                setReminderDlg({
                  title: `Reminder pickup/delivery: ${form.customer_name || "—"}`,
                  text: "",
                  date: prefillDate,
                  time_of_day: form.pickup_time || "08:00",
                  priority: "normaal",
                });
              }} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="pickup-reminder-add">
                <Plus className="w-3 h-3 mr-1" /> Reminder toevoegen
              </Button>
            )}
          </div>
          {reminders.length === 0 ? (
            <p className="text-sm text-gray-500">Geen reminders gekoppeld. Klik op &quot;+ Reminder toevoegen&quot; hierboven om er een aan te maken.</p>
          ) : (
            <ul className="space-y-2">
              {reminders.map((r) => (
                <li key={r.id} className="border border-gray-200 p-3 text-sm" data-testid={`pickup-reminder-${r.id}`}>
                  <div className="flex justify-between"><span className="font-bold">{r.title}</span><span className="text-xs">{r.date} {r.time_of_day || ""}</span></div>
                  {r.text && <p className="text-xs text-gray-600 mt-1">{r.text}</p>}
                </li>
              ))}
            </ul>
          )}

          {/* Operationele P0 — call-logs gekoppeld aan deze pickup */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h3 className="font-chivo font-black text-sm text-[#0A192F] flex items-center gap-2 mb-2"><PhoneIncoming className="w-4 h-4 text-[#0055FF]" />Telefoonregistraties ({callLogs.length})</h3>
            {callLogs.length === 0 ? (
              <p className="text-xs text-gray-500">Geen telefoonregistraties gekoppeld. Koppel een nieuwe call-log via de Call Log-module.</p>
            ) : (
              <ul className="space-y-2" data-testid="pickup-call-logs-list">
                {callLogs.map((c) => (
                  <li key={c.id} className="border border-gray-200 p-2 text-xs" data-testid={`pickup-call-log-${c.id}`}>
                    <div className="flex justify-between"><span className="font-bold">{c.subject}</span><span className="text-gray-500">{new Date(c.created_at).toLocaleString("nl-NL")}</span></div>
                    <div className="text-gray-600">{c.customer_name} · {c.phone || "—"} · {c.priority}</div>
                    {c.summary && <p className="italic text-gray-500 mt-1">{c.summary}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notities" className="bg-white border border-gray-200 p-6 mt-2">
          <h2 className="font-chivo font-black text-lg text-[#0A192F] mb-3 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#0055FF]" />Interne notities</h2>
          {canEdit && (
            <div className="bg-[#F8F9FA] border border-gray-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
              <Input placeholder="Notitie..." value={newNote} onChange={(e)=>setNewNote(e.target.value)} className="rounded-none" data-testid="pickup-note-input"/>
              <Button onClick={onAddNote} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="pickup-note-add">Toevoegen</Button>
            </div>
          )}
          {notes.length === 0 ? <p className="text-sm text-gray-500">Geen notities.</p> : (
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.id} className="border border-gray-200 p-3 text-sm">
                  <div className="flex justify-between text-xs text-gray-500"><span>{n.author_email}</span><span>{fmtTs(n.created_at)}</span></div>
                  <p className="mt-1">{n.text}</p>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      <div className="bg-white border border-dashed border-gray-200 p-4 text-[11px] text-gray-500 italic" data-testid="pickup-audit-footer">
        Acties worden geaudit (PDF export, werkbon, e-mail naar klant, WhatsApp-tekst). Bekijk de audit-trail in Backoffice &gt; Audit log.
      </div>

      {/* Operationele P0 — Reminder add dialog */}
      {reminderDlg && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) setReminderDlg(null); }}>
          <DialogContent className="rounded-xl max-w-xl" data-testid="pickup-reminder-dialog">
            <DialogHeader>
              <DialogTitle className="font-chivo font-black text-xl text-[#082c50]">Reminder toevoegen voor pickup</DialogTitle>
              <DialogDescription>Wordt automatisch gekoppeld aan deze pickup/delivery.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase font-bold">Titel *</Label>
                <Input value={reminderDlg.title} onChange={(e) => setReminderDlg({ ...reminderDlg, title: e.target.value })} className="rounded-xl mt-1" data-testid="pickup-reminder-dlg-title" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[11px] uppercase font-bold">Datum *</Label>
                  <DatePickerField value={reminderDlg.date} onChange={(d) => setReminderDlg({ ...reminderDlg, date: d })} testId="pickup-reminder-dlg-date" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase font-bold">Tijd</Label>
                  <Input type="time" value={reminderDlg.time_of_day || ""} onChange={(e) => setReminderDlg({ ...reminderDlg, time_of_day: e.target.value })} className="rounded-xl mt-1" data-testid="pickup-reminder-dlg-time" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase font-bold">Prioriteit</Label>
                  <Select value={reminderDlg.priority} onValueChange={(v) => setReminderDlg({ ...reminderDlg, priority: v })}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="pickup-reminder-dlg-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="hoog">Hoog</SelectItem>
                      <SelectItem value="normaal">Normaal</SelectItem>
                      <SelectItem value="laag">Laag</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase font-bold">Tekst (optioneel)</Label>
                <Textarea rows={2} value={reminderDlg.text} onChange={(e) => setReminderDlg({ ...reminderDlg, text: e.target.value })} className="rounded-xl mt-1" data-testid="pickup-reminder-dlg-text" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 mt-3">
              <Button variant="outline" onClick={() => setReminderDlg(null)} className="rounded-xl text-xs">Annuleren</Button>
              <Button onClick={async () => {
                if (!reminderDlg.title.trim()) return toast.error("Titel is verplicht");
                if (!reminderDlg.date) return toast.error("Datum is verplicht");
                try {
                  await adminApi.post("/admin/reminders", {
                    title: reminderDlg.title.trim(),
                    text: reminderDlg.text.trim() || null,
                    date: reminderDlg.date,
                    time_of_day: reminderDlg.time_of_day || null,
                    priority: reminderDlg.priority,
                    link_type: "pickup",
                    link_id: pickupId,
                  });
                  toast.success("Reminder aangemaakt en gekoppeld aan pickup");
                  setReminderDlg(null);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
                }
              }} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="pickup-reminder-dlg-save">
                Opslaan
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
